import { useTranslation } from "react-i18next";
import { Box, Stack, Typography, useTheme } from "@mui/material";
import { DrawerContainer } from "../components/DrawerContainer";
import { metricValue, bySource, isEmpty, fmtDate, mergeNonEmpty } from "./careerUtils";

// Fila label (bold) + valor
const Field = ({ label, value, preformatted }) => {
  if (isEmpty(value)) return null;
  return (
    <Box>
      <Typography fontSize={11} fontWeight={700} color="secondary">{label}</Typography>
      <Typography fontSize={12} color="oxfordBlue.main" sx={preformatted ? { whiteSpace: "pre-line" } : undefined}>
        {value}
      </Typography>
    </Box>
  );
};

// Bloque/tarjeta por entrada dentro de la modal
const Block = ({ heading, children }) => {
  const theme = useTheme();
  return (
    <Box
      sx={{
        border: `1px solid ${theme.palette.blueGray200.main}`,
        borderRadius: "10px",
        padding: "12px 16px",
      }}
    >
      {heading && (
        <Typography fontSize={13} fontWeight={700} color="oxfordBlue.main" mb={1.2} pb={1} sx={{ borderBottom: `1px solid ${theme.palette.blueGray200.main}`, mx: "-16px", px: "16px" }}>
          {heading}
        </Typography>
      )}
      <Stack spacing={1.2}>{children}</Stack>
    </Box>
  );
};

/**
 * Modal de detalle de las secciones de Carrera. `type` define qué sección se
 * muestra: "improvement" | "disciplinary" | "turnover" | "feedback".
 */
export const CareerDetailModal = ({ open, onClose, type, career }) => {

  const [t] = useTranslation("investigation");
  const theme = useTheme();

  const data = career?.data || [];

  const titles = {
    improvement: t("involved.career_improvement_plan"),
    disciplinary: t("involved.career_disciplinary_measures"),
    turnover: t("involved.career_turnover"),
    feedback: t("involved.career_feedback_360"),
  };

  const renderContent = () => {
    switch (type) {
      // ── Improvement Plan ──────────────────────────────────────────────
      case "improvement": {
        const pip = career?.pip_detail || [];
        return (
          <Block>
            <Field label={t("involved.career_field_pip_start")} value={fmtDate(metricValue(pip, "START_DATE_IMPROVEMENT_PLAN"))} />
            <Field label={t("involved.career_field_pip_end")} value={fmtDate(metricValue(pip, "END_DATE_IMPROVEMENT_PLAN"))} />
            <Field label={t("involved.career_field_pip_description")} value={metricValue(pip, "DESCRIPTION_IMPROVEMENT_PLAN")} preformatted />
            <Field label={t("involved.career_field_pip_output")} value={metricValue(pip, "OUTPUT_PLAN_IMPROVEMENT_PLAN")} preformatted />
          </Block>
        );
      }

      // ── Medidas disciplinarias ────────────────────────────────────────
      case "disciplinary": {
        const measures = bySource(data, "LK_PE_DISCIPLINARY_MEASURES");
        return (
          <>
            {measures.map((m, i) => (
              <Block key={i} heading={m?.REASON || t("involved.career_disciplinary_measures")}>
                <Field label={t("involved.career_field_end_date")} value={fmtDate(m?.END_DATE)} />
                <Field label={t("involved.career_field_days")} value={m?.QUANTITY_DAYS} />
                <Field label={t("involved.career_field_reason")} value={m?.REASON} />
                <Field label={t("involved.career_field_failure")} value={m?.FAILURE} />
                <Field label={t("involved.career_field_rejected")} value={m?.REJECTED} />
              </Block>
            ))}
          </>
        );
      }

      // ── Turnover ──────────────────────────────────────────────────────
      case "turnover": {
        const exits = career?.exit_history || [];
        return (
          <>
            {exits.map((e, i) => (
              <Block key={i} heading={e?.FULL_NAME}>
                <Field label={t("involved.career_field_end_date")} value={fmtDate(e?.LAST_WORKING_DATE)} />
                <Field label={t("involved.career_field_termination_type")} value={e?.TERMINATION_TYPE} />
                <Field label={t("involved.career_field_turnover_type")} value={e?.TURNOVER_TYPE} />
                <Field label={t("involved.career_field_exit_reason")} value={e?.EXIT_REASON} />
                <Field label={t("involved.career_field_other_reasons")} value={mergeNonEmpty([e?.TERMINATION_REASON, e?.OTHER_REASON])} />
                <Field
                  label={t("involved.career_field_interview")}
                  value={mergeNonEmpty([e?.EXIT_INTERVIEW_REASONS, e?.EXIT_INTERVIEW_MAIN_REASON, e?.EXIT_INTERVIEW_OTHER_REASON])}
                  preformatted
                />
              </Block>
            ))}
          </>
        );
      }

      // ── Feedback 360 ──────────────────────────────────────────────────
      case "feedback": {
        const feedback = bySource(data, "LK_PE_FEEDBACK_360")
          .slice()
          .sort((a, b) => (Date.parse(b?.ANSWER_DATE) || 0) - (Date.parse(a?.ANSWER_DATE) || 0));
        return (
          <>
            {feedback.map((f, i) => (
              <Block key={i} heading={f?.SENDER_FULL_NAME}>
                <Field label={t("involved.career_field_status")} value={f?.STATUS} />
                <Field label={t("involved.career_field_answer_date")} value={fmtDate(f?.ANSWER_DATE)} />
                <Field label={t("involved.career_field_period")} value={f?.PERIOD} />
                <Field label={t("involved.career_field_strengths")} value={f?.STRENGTHS} preformatted />
                <Field label={t("involved.career_field_opportunities")} value={f?.OPPORTUNITIES} preformatted />
              </Block>
            ))}
          </>
        );
      }

      default:
        return null;
    }
  };

  return (
    <DrawerContainer
      open={open}
      onClose={onClose}
      borderColor={theme.palette.primary.main}
      maxWidth="480px"
    >
      <Box padding="10px 20px 20px 20px">
        <Typography fontSize={24} fontWeight={600} color="primary" marginBottom={2}>
          {titles[type] || ""}
        </Typography>
        <Stack spacing={1.5}>
          {renderContent()}
        </Stack>
      </Box>
    </DrawerContainer>
  );
};
